import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, readJson } from "../lib/api";
import { useAuth } from "../hooks/useAuth";
import { useOnlineStatus } from "../hooks/useOnlineStatus";
import { useToast } from "../components/Toast";
import { getDeviceId, setPendingMode } from "../lib/storage";
import { isWorkspacePaused, outboxCount, pullSince, startBackgroundSync } from "../lib/sync";
import { listCachedRuns, type CachedRun } from "../lib/outbox";
import type { LiveSession, RunRow } from "../lib/types";
import { ENGAGE_ME_KEY, formatMoney, setEngageTarget, useEngageMe } from "../lib/engage";
import { clockOut } from "../lib/line";
import { TargetRing } from "../components/TargetRing";
import { TargetSheet } from "../components/TargetSheet";
import { useOrgSettingsQuery, useSites, useUnits } from "../hooks/useUnits";
import { getDeviceSite, setDeviceSite } from "../lib/site";
import { fmtRate, fmtWeight } from "../lib/units";
import { ContractOffers, FeedbackInbox, ProfilePicture, StatusBar } from "../components/CrewPanels";

function timeAgo(epochSeconds: number): string {
  const s = Date.now() / 1000 - epochSeconds;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function HomeDashboard() {
  const { player, orgId } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const online = useOnlineStatus();
  const queryClient = useQueryClient();
  const deviceId = useMemo(() => getDeviceId(), []);
  const sites = useSites();
  const [deviceSite, setDeviceSiteState] = useState<number | null>(() => getDeviceSite());
  const [queued, setQueued] = useState(0);
  const [paused, setPaused] = useState(false);
  const [cachedRuns, setCachedRuns] = useState<CachedRun[]>([]);
  const [targetSheetOpen, setTargetSheetOpen] = useState(false);
  const units = useUnits();
  const seasonCards = useOrgSettingsQuery().data?.season_cards === true;

  // M6 engagement layer — feature-degrades silently: `engageMe` undefined
  // (404/offline/not-yet-deployed) just means the ring+chips strip below
  // renders nothing at all, never a skeleton or an error state.
  const { data: engageMe } = useEngageMe();

  async function pickTarget(grams: number) {
    try {
      await setEngageTarget(grams);
      queryClient.invalidateQueries({ queryKey: ENGAGE_ME_KEY });
      setTargetSheetOpen(false);
      toast(`Target set to ${fmtWeight(grams, units)}`, "ok");
    } catch {
      toast("Couldn't set target — try again", "err");
    }
  }
  async function clearTarget() {
    try {
      await setEngageTarget(null);
      queryClient.invalidateQueries({ queryKey: ENGAGE_ME_KEY });
      setTargetSheetOpen(false);
      toast("Target cleared", "ok");
    } catch {
      toast("Couldn't clear target — try again", "err");
    }
  }

  const { data: live } = useQuery({
    queryKey: ["live-mine"],
    queryFn: () => api.get("/api/live/mine").then((r) => readJson<{ live: LiveSession | null }>(r)),
    enabled: online,
    refetchInterval: online ? 20000 : false,
  });

  const { data: runs } = useQuery({
    queryKey: ["runs-recent"],
    queryFn: () => api.get("/api/runs?limit=10").then((r) => readJson<RunRow[]>(r)),
    enabled: online,
  });

  async function refreshQueueDisplay() {
    setQueued(await outboxCount());
    setPaused(isWorkspacePaused());
    setCachedRuns(await listCachedRuns());
  }

  useEffect(() => {
    refreshQueueDisplay();
    const stop = startBackgroundSync(deviceId, () => {
      refreshQueueDisplay();
      queryClient.invalidateQueries({ queryKey: ["runs-recent"] });
    });
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  useEffect(() => {
    if (!online || orgId === null) return;
    pullSince(orgId).then(({ hasNew }) => {
      if (hasNew) queryClient.invalidateQueries({ queryKey: ["runs-recent"] });
    });
  }, [online, orgId, queryClient]);

  async function discardLive() {
    if (!live?.live) return;
    if (!confirm("Discard this session? The timer data will be lost.")) return;
    await api.postJson(`/api/live/${live.live.id}/cancel`, {});
    // M6 wave-2: this session might have been clocked into a line station
    // (TrimPage tracks that locally, invisible from here) — clockOut() is a
    // safe no-op (swallows a "not clocked in" 400, never throws) when there
    // was nothing open, so it's cheap insurance against a stray open
    // clock-in outliving the discarded run.
    clockOut();
    queryClient.invalidateQueries({ queryKey: ["live-mine"] });
    toast("Session discarded", "ok");
  }

  const totalRunsShown = (runs?.length || 0) + cachedRuns.length;

  return (
    <>
      <div className="hero row" style={{ gap: 14, alignItems: "center" }}>
        <ProfilePicture />
        <div>
          <h1>Hey {player?.name}</h1>
          <p>Runs sync live — start a quick trim whenever you&rsquo;re ready.</p>
        </div>
      </div>

      {/* Multi-site orgs only: which facility this device is in. Every run
          logged here is tagged with it. Hidden entirely for single-site orgs. */}
      {sites.length > 0 && (
        <div className="panel" data-testid="site-picker">
          <div className="field-label">This device is at</div>
          <div className="chip-grid">
            {sites.map((st) => (
              <button
                key={st.id} type="button"
                data-testid={`device-site-${st.id}`}
                className={`chip${deviceSite === st.id ? " active" : ""}`}
                onClick={() => { setDeviceSite(st.id); setDeviceSiteState(st.id); }}
              >
                {st.name}
              </button>
            ))}
          </div>
          {deviceSite === null && (
            <div className="muted small" style={{ marginTop: 6 }}>Pick a site so your runs count toward the right facility.</div>
          )}
        </div>
      )}

      {engageMe && (
        <div className="engage-strip" data-testid="engage-strip">
          <TargetRing
            todayGrams={engageMe.today.grams}
            target={engageMe.target}
            onTap={() => setTargetSheetOpen(true)}
          />
          <div className="engage-chip-col">
            <span
              className={`engage-chip streak-chip${engageMe.streak.freeze_used_this_week ? " has-freeze" : ""}`}
              data-testid="streak-chip"
              title={`${engageMe.streak.above_avg_days} days above your average`}
            >
              🔥 {engageMe.streak.days}d{engageMe.streak.freeze_used_this_week ? " ❄️" : ""}
            </span>
            {engageMe.earnings && (
              <span className="engage-chip earnings-chip" data-testid="earnings-chip">
                Earned today {formatMoney(engageMe.earnings.today_cents, engageMe.earnings.currency)}
              </span>
            )}
            <span className="engage-chip level-chip" data-testid="level-chip">
              {engageMe.level.title}
              {engageMe.level.next_title && engageMe.level.next_at_grams ? (
                <span className="level-progress">
                  {Math.min(100, Math.round((engageMe.level.lifetime_grams / engageMe.level.next_at_grams) * 100))}%
                  {" "}to {engageMe.level.next_title}
                </span>
              ) : null}
            </span>
          </div>
        </div>
      )}

      {seasonCards && (
        <Link to="/season" className="engage-chip season-chip" data-testid="season-link" style={{ display: "inline-block", marginBottom: 12 }}>
          Your season card →
        </Link>
      )}

      {online && <><ContractOffers /><FeedbackInbox /></>}

      <TargetSheet
        open={targetSheetOpen}
        currentGrams={engageMe?.target.grams ?? null}
        onPick={pickTarget}
        onClear={clearTarget}
        onClose={() => setTargetSheetOpen(false)}
      />

      {live?.live && (
        <div className="panel resume-card" style={{ marginBottom: 12 }}>
          <h2>Session in progress <span className="pill live-pill">LIVE</span></h2>
          <div className="muted small">
            {live.live.mode === "full" ? "Full batch" : "Quick"} · {live.live.strain_name || "no strain"} · started{" "}
            {Math.floor(live.live.elapsed_active_ms / 60000)}m ago
            {live.live.stop_pending_at
              ? ` · stopped — ${Math.max(0, Math.ceil((live.live.resume_left_ms || 0) / 60000))}m left to resume`
              : ""}
          </div>
          <div className="btn-row" style={{ marginTop: 12 }}>
            <button type="button" className="btn btn-ghost" onClick={discardLive}>Discard</button>
            <button type="button" className="btn btn-primary" onClick={() => navigate({ to: "/trim" })}>Resume</button>
          </div>
        </div>
      )}

      {online && !live?.live && <StatusBar />}

      <div className="mode-grid" style={{ marginBottom: 12 }}>
        <button
          type="button"
          className="mode-card quick"
          data-testid="start-quick-trim"
          onClick={() => {
            setPendingMode("quick");
            navigate({ to: "/trim" });
          }}
        >
          <div>
            <span className="badge">Fast</span>
            <div className="title">Quick log</div>
            <div className="sub">Pick strain, trim, weigh, done. No start weight. Best for routine work.</div>
          </div>
          <div className="btn btn-primary">Start quick</div>
        </button>

        <button
          type="button"
          className="mode-card full"
          data-testid="start-full-batch"
          onClick={() => {
            setPendingMode("full");
            navigate({ to: "/trim" });
          }}
        >
          <div>
            <span className="badge">Full log</span>
            <div className="title">Full batch</div>
            <div className="sub">Start weight photo, trim, end weight photo, QA photo. Yield percent, batch report.</div>
          </div>
          <div className="btn btn-ghost">New batch</div>
        </button>
      </div>

      <div className="panel">
        <h2>Sync <span className="pill" data-testid="sync-pill">{queued > 0 ? `${queued} queued` : "ready"}</span></h2>
        {queued > 0 && paused ? (
          <div className="warn-box" data-testid="sync-paused">
            Workspace paused — ask your owner to renew. Your entries are saved on this device.
          </div>
        ) : (
          <div className="muted small">
            {queued > 0 ? "Will sync automatically once you're back online." : "Nothing queued."}
          </div>
        )}
      </div>

      <div className="panel">
        <h2>Your recent runs <span className="pill">{totalRunsShown}</span></h2>
        {cachedRuns.map((r) => (
          <div className="mini-run pending" key={r.client_uuid} data-testid="run-pending">
            <div>
              <div className="who">{r.player_name} · {r.strain_name || "unnamed"}</div>
              <div className="meta">queued — will sync · {r.mode}</div>
            </div>
            <div className="grams">{r.end_grams ? fmtWeight(r.end_grams, units, 1) : "—"}</div>
          </div>
        ))}
        {(runs || []).map((r) => (
          <div className="mini-run" key={r.id} data-testid="run-row">
            <div>
              <div className="who">{r.player_name} · {r.strain_name || "unnamed"}</div>
              <div className="meta">
                {timeAgo(r.created_at)}{r.gpm ? ` · ${fmtRate(r.gpm, units, 1)}` : ""} · {r.mode}
              </div>
            </div>
            <div className="grams">{r.end_grams ? fmtWeight(r.end_grams, units, 1) : "—"}</div>
          </div>
        ))}
        {totalRunsShown === 0 && (
          <div className="muted small">{online ? "No runs yet. Start one!" : "Offline — recent list unavailable."}</div>
        )}
      </div>
    </>
  );
}
